import { silently, track } from "./analytics";
import { rememberLogin } from "./login";
import type { AuthMethod } from "./events";

export type SsoProvider = Exclude<AuthMethod, "email">;

/** The page the SSO button was on. */
export type SsoFlow = "login" | "signup";

/** The signed-in user as the SSO return reads it. Any field may be missing. */
export interface SsoReturnUser {
  companies?: ReadonlyArray<{
    onboardingCompletedAt?: string | null;
  } | null> | null;
}

const SSO_ATTEMPT_KEY = "tender-app:analytics-sso-attempt";
const SSO_ATTEMPT_MAX_AGE_MS = 15 * 60 * 1000;
const SSO_PROVIDERS: ReadonlyArray<string> = ["google", "microsoft"];

const isSsoProvider = (value: unknown): value is SsoProvider =>
  typeof value === "string" && SSO_PROVIDERS.includes(value);

/**
 * The backend sends the browser back into the app after an SSO login without
 * saying a login just happened, so the click is remembered for
 * `consumeSsoReturn` to find on the way back in.
 */
export const rememberSsoAttempt = (
  provider: SsoProvider,
  flow: SsoFlow,
): void =>
  silently(() => {
    sessionStorage.setItem(
      SSO_ATTEMPT_KEY,
      JSON.stringify({ provider, flow, startedAt: Date.now() }),
    );
  });

/** The provider of a remembered attempt recent enough to be this login. */
const recentAttemptProvider = (stored: string): SsoProvider | undefined => {
  const attempt: unknown = JSON.parse(stored);
  if (typeof attempt !== "object" || attempt === null) return undefined;

  const { provider, startedAt } = attempt as Record<string, unknown>;
  if (!isSsoProvider(provider) || typeof startedAt !== "number") {
    return undefined;
  }

  const age = Date.now() - startedAt;
  return age >= 0 && age <= SSO_ATTEMPT_MAX_AGE_MS ? provider : undefined;
};

/**
 * Called each time the authenticated app loads. A remembered SSO attempt means
 * this load is the return from the provider: the attempt is removed and the
 * login or signup it completed is sent once. A failed attempt that went back
 * to the login page simply stays until it is too old to count.
 *
 * The page the button was on does not decide which event this is, because SSO
 * from the login page creates an account for an email it has not seen. A new
 * account lands in onboarding, so it has no company that finished it yet.
 */
export const consumeSsoReturn = (me?: SsoReturnUser | null): void =>
  silently(() => {
    const stored = sessionStorage.getItem(SSO_ATTEMPT_KEY);
    if (stored === null) return;
    // Removed before anything is sent: an attempt that could not be removed
    // would be sent again on every navigation until it expired.
    sessionStorage.removeItem(SSO_ATTEMPT_KEY);

    const provider = recentAttemptProvider(stored);
    if (!provider) return;

    const isExistingAccount =
      me?.companies?.some((company) => company?.onboardingCompletedAt) ?? false;
    // A login is sent once the company is synced, so it carries the account.
    // A new account has no company yet, so its signup goes out right away.
    if (isExistingAccount) rememberLogin(provider);
    else track("auth.signup.completed", { method: provider });
  });
